import type {GetServerSideProps, GetServerSidePropsResult} from 'next';
import Head from 'next/head';
import {z} from 'zod';
import type {ReportSummaryType} from '../../../../../core/entities/reportAnalytics/schema';
import {getReportSummary} from '../../../../../core/repositories/reportSummaryRepository';
import {
  type GetUserDataForAnalyticsResponseType,
  getUserDataForAnalytics,
} from '../../../../../core/repositories/usersMeRepository';
import {validateCookie} from '../../../../../core/usecases/auth/checkOauthTokenExistence';
import {ReportAnalyticsResultPage} from '../../../../../features/reportAnalytics/components/ReportAnalyticsResultPage';
import {withHandler} from '../../../../../server/token/withHandler';
import {ScriptForGA} from '../../../../../shared/components/ScriptForGA';
import {NoLeftSidebarLayout} from '../../../../../shared/components/layouts/NoLeftSidebarLayout';
import type {NextPageWithLayout} from '../../../../_app';

type Props = {
  reportSummaryData?: ReportSummaryType;
  userDataForAnalytics?: GetUserDataForAnalyticsResponseType;
};
const QueryParameter = z.object({
  companyID: z.string(),
  departmentID: z.string(),
  reportType: z.string(),
});

type QueryParameter = z.infer<typeof QueryParameter>;

export const getServerSideProps: GetServerSideProps = withHandler<
  Props,
  'withOptionalSession' | 'withQuery',
  QueryParameter
>(
  {auth: 'optional', queryValidator: QueryParameter},
  async (context): Promise<GetServerSidePropsResult<Props>> => {
    const {cookies} = context.req;
    const {companyID, departmentID, reportType} = context.validatedQuery;
    const response = await getReportSummary({
      companyID: Number.parseInt(companyID, 10),
      departmentID: Number.parseInt(departmentID, 10),
      queryParams: {
        reportType,
      },
    });

    const {isValid} = validateCookie(cookies);
    if (!isValid) {
      return {
        props: {
          reportSummaryData: response.ok ? response.value : undefined,
        },
      };
    }

    const userDataResponse = await getUserDataForAnalytics({cookies});
    if (!userDataResponse.ok) {
      return {
        props: {
          reportSummaryData: response.ok ? response.value : undefined,
        },
      };
    }

    return {
      props: {
        reportSummaryData: response.ok ? response.value : undefined,
        userDataForAnalytics: userDataResponse.value,
      },
    };
  },
);

const Page: NextPageWithLayout<Props> = ({
  reportSummaryData,
  userDataForAnalytics,
}) => {
  return (
    <>
      <Head>
        {/* Meta Pixel Code 登録完了 */}
        <script>{`fbq('track', 'CompleteRegistration');`}</script>
        {/* End Meta Pixel Code */}
      </Head>
      <ReportAnalyticsResultPage reportSummaryData={reportSummaryData} />
      <ScriptForGA userDataForAnalytics={userDataForAnalytics} />
    </>
  );
};

export default Page;

Page.getLayout = (page) => <NoLeftSidebarLayout>{page}</NoLeftSidebarLayout>;
